/**
 * SolverState — pure-data game state for the perfect-play solver.
 *
 * This module provides the state representation and Zobrist-style hashing
 * foundation for a brute-force DFS solver that proves boolean winnability.
 *
 * Design invariants:
 *  - State is treated as immutable. `applyCubeCleared`, HP decrements, etc.
 *    return new state objects (with typed arrays copied) so the DFS caller
 *    can keep the previous state for backtracking.
 *  - The stack is stored in canonical (sorted) order so permutations hash
 *    identically — the stack is conceptually a multiset.
 *  - Hashing is Zobrist-style: every observable feature XORs a precomputed
 *    64-bit constant into a bigint, so single-field updates can re-hash
 *    incrementally (XOR-out old, XOR-in new).
 *
 * This module does NOT enumerate legal actions, simulate laps, or run DFS.
 * It's the foundation that later stages will build on.
 */

import type { ImageLevel } from '../../levels/types';
import { resolveLevelDifficulty, resolveLevelSeed, NEW_LEVEL_DIFFICULTY_LEVEL } from '../../levels/levelQueueParams';
import type { CellStack, LockedDoorDef, BlockadeWallDef, BarricadeDef, MegaBlockDef } from '../../types/board';
import { generateRawQueue, type AttackerQueueEntry, type DifficultyConfig } from '../LevelGenerator';

// ── Types ────────────────────────────────────────────────────────────────────

/**
 * Pure-data representation of an attacker. Unlike {@link AttackerData}, this
 * has no mutable `state` field — location is tracked by which array the
 * attacker lives in (queue lane, conveyor, or stack).
 */
export interface SolverAttacker {
  /** Stable identifier that maps back to the original queue position. */
  id: number;
  /** Per-orb color and remaining ammo. For hammers, a single orb with color=-1, ammo=0. */
  orbs: { color: number; ammo: number }[];
  /** How many physical slots this attacker occupies (= orbs.length for now). */
  slotCount: number;
  /** Frozen counter — 0/undefined = not frozen, N = needs N exhaustions to thaw. */
  frozen?: number;
  /** True if this is a hammer (breaks barricades instead of shooting cubes). */
  isHammer?: boolean;
  /** True if the orbs' colors are hidden until the first shot. */
  mystery?: boolean;
  /**
   * IN-FLIGHT RING ONLY: how many player actions (`dispatch` or `settle`) have
   * been taken since this rider's own dispatch resolved. Absent = 0. Stored
   * CAPPED at `conveyorCap` (the forced-arrival bound — see
   * `docs/ordered-flow-semantics.md`), which keeps the hash domain finite; in
   * the model the invariant `age <= conveyorCap` holds anyway, so the cap is
   * lossless there. Age is solver state: it decides when the ring's front
   * rider is force-arrived, so {@link attackerHash} folds it in. Queue and
   * stack attackers NEVER carry it — an arrival strips it (the wait is over)
   * and a dispatch starts a fresh rider at 0 (field absent).
   */
  age?: number;
}

/**
 * Structural level metadata — constants that never change during play.
 * Kept separate from {@link SolverState} so clones can share the same ref.
 */
export interface SolverLevelMeta {
  /** Cells (row*boardWidth+col) covered by snake blocks. */
  snakeCells: Set<number>;
  /** Cells covered by doors (shots pass through; doors are transparent). */
  doorCells: Set<number>;
  /** Map from cell → doorId (for cells covered by doors). */
  doorIdByCell: Record<number, number>;
  /** doorId → colorIndex (for HP decrement on matching color clear). */
  doorColorById: Record<number, number>;
  /** Cells belonging to each mega block, keyed by megaId. */
  megaCellsById: Record<number, number[]>;
  /** Cell → megaId (for cells covered by mega blocks). */
  megaIdByCell: Record<number, number>;
  /** Cells belonging to each barricade, keyed by barricadeId (insertion order). */
  barricadeCellsById: Record<number, number[]>;
  /** Cell → barricadeId (for cells covered by barricades). */
  barricadeIdByCell: Record<number, number>;
  /**
   * Ordered list of barricade IDs (insertion order). Hammers pick the lowest-id
   * barricade still having HP > 0.
   */
  barricadeIds: number[];
  /** wallId → { side, startCell, length, colorIndex }. */
  wallInfoById: Record<number, { side: 'top' | 'bottom' | 'left' | 'right'; startCell: number; length: number; colorIndex: number }>;
}

/**
 * The full game state. A single `SolverState` captures everything observable
 * that can affect the outcome of the rest of the game.
 *
 * Under ordered flow (`docs/ordered-flow-semantics.md`) one dispatch resolves
 * a whole lap atomically; a survivor rides the in-flight ring until it
 * arrives on the stack. There is no mid-lap conveyor *position* to model —
 * only ring membership, FIFO order, and each rider's action-age.
 */
export interface SolverState {
  /**
   * Packed cube colors, one value per cell in row-major order.
   *  - 0        = empty cell
   *  - 1..N     = color index + 1 (so "color 0 = red" stores as 1)
   * Using Uint16Array keeps per-state memory low and copying cheap.
   */
  board: Uint16Array;
  boardWidth: number;
  boardHeight: number;

  /**
   * Ordered attackers per queue lane, front (index 0) first. queuePtr is
   * implicit — removing an attacker means splicing index 0 out of the lane.
   * Frozen status lives on the attacker via `SolverAttacker.frozen`.
   */
  queueLanes: SolverAttacker[][];

  /**
   * The IN-FLIGHT RING: survivors currently riding the belt, oldest first.
   * `conveyor[0]` is the next attacker to arrive on the stack.
   *
   * ORDERED-FLOW MODEL (rev. 2026-09-03, `docs/ordered-flow-semantics.md`): a
   * dispatch resolves the attacker's whole lap and then either culls it (ammo
   * exhausted) or appends it to the TAIL of this ring at age 0. It leaves the
   * ring only by *arriving* on the stack, and there are exactly three causes:
   * an explicit `settle` (FIFO), the AGE BOUND (a rider whose age reaches
   * `conveyorCap` is force-arrived before the next player action), or the
   * quiescence drain. The ring is capped by {@link conveyorCap}, and — unlike
   * the retired burst model — that cap REFUSES a dispatch that does not fit:
   * dispatch is legal iff the ring has room.
   *
   * Three retired readings of this field, kept here so none comes back:
   *   - the *parking pen* (pre-2026-09-01): a survivor sat here until the
   *     player scheduled a free `advance`, giving 10 buffer slots the game
   *     never had;
   *   - the *forced-advance* model (`fa81b6b1`): the belt was permanently
   *     empty because a survivor went straight to the stack in the same step
   *     as its lap, giving 5 buffer slots when the game has 10;
   *   - the *burst* model (2026-09-01→03): a tap into a full belt forced
   *     "belt-pressure" arrivals instead of being refused. Arthur playtested
   *     it, lost to a mid-lap forced eviction, and rejected the mechanic —
   *     the live game refuses the tap, and the model refuses the dispatch.
   *
   * The belt is a real FIFO buffer and the stack constraint binds at ARRIVAL,
   * not at dispatch. {@link hashState} folds the ring in positionally, and
   * each rider's {@link SolverAttacker.age} is part of its attacker hash, so
   * ring order AND deferral budget are both state identity.
   */
  conveyor: SolverAttacker[];

  /** Stack (waiting slots). Stored sorted canonically — see {@link canonicalizeStack}. */
  stack: SolverAttacker[];

  /** Current HP of each locked door, keyed by doorId. */
  doorHp: Record<number, number>;
  /** Current HP of each blockade wall, keyed by wallId. */
  wallHp: Record<number, number>;
  /** Per-cell barricade HP, keyed by (row * boardWidth + col). */
  barricadeHp: Record<number, number>;
  /** Current HP of each mega block, keyed by megaId. */
  megaHp: Record<number, number>;

  /** Structural level metadata — immutable across play (shared ref). */
  meta: SolverLevelMeta;

  /** Count of unused hammers anywhere (queue + conveyor + stack). */
  remainingHammers: number;

  /** Per-level conveyor capacity (maximum slotCount sum on the conveyor). */
  conveyorCap: number;
  /** Per-level stack capacity (maximum slotCount sum in the stack). */
  stackCap: number;
  /**
   * Incremental Zobrist hash over only the board-visible state (cells +
   * barricade HP — the inputs that drive `firstVisible`). Maintained by
   * every board/barricade mutation in `AtomicSimulator` so the reach-colors
   * cache can key on it in O(1) rather than re-scanning the whole board.
   *
   * Never mutates during queue / conveyor / stack / door HP / wall HP /
   * mega HP changes — those are the axes we WANT to collide for the cache.
   *
   * Populated by `initialState`; updated in-place inside the lap simulator
   * (which owns a fresh copy of the state's mutable fields anyway).
   */
  boardHash: bigint;
}

/**
 * Send an attacker around the belt. The attacker's current location (a queue
 * lane head or a stack slot) is derivable from the state, so we don't need
 * separate `dispatchQueue` / `dispatchStack` variants.
 */
export type DispatchAction = { type: 'dispatch'; attackerId: number };

/**
 * "A lap completed with nothing else happening": the in-flight ring's OLDEST
 * rider arrives on the stack. Legal exactly when the ring is non-empty.
 *
 * `settle` is emphatically NOT the retired `advance`. `advance` moved a
 * survivor off a phantom post-lap holding pen the game never had — a free
 * extra buffer. `settle` is the belt's oldest rider finishing the lap it was
 * always going to finish; in live play the clock generates it, and in the
 * model it is the player's "I waited". A settle is a player action, so it
 * ages every other rider by one; a settle chosen while the front rider is at
 * the age bound simply IS that rider's (otherwise forced) arrival. See
 * `docs/ordered-flow-semantics.md` §2.
 */
export type SettleAction = { type: 'settle' };

/**
 * The solver's action alphabet under ORDERED FLOW
 * (`docs/ordered-flow-semantics.md`, normative).
 *
 * Two actions, and the same two the live game emits: `dispatch` and `settle`.
 * A third event, `arrive`, exists but is never *chosen* — it is produced by a
 * settle, by the age bound (the ring's front rider reaching age `conveyorCap`
 * is force-arrived before the next player action), or by the quiescence drain.
 */
export type SolverAction = DispatchAction | SettleAction;

/**
 * Read-only superset of {@link SolverAction} that still parses the retired
 * `{ type: 'advance' }` member.
 *
 * Nothing in the solver ever produces one. It exists so witnesses archived
 * before the forced-advance change stay *readable* — the replay paths detect
 * an advance-bearing witness and refuse it with "re-prove at the current
 * solverRev" instead of failing to parse.
 *
 * The three witness vintages, and how a reader tells them apart:
 *   - contains `advance`            → parking model, REJECT;
 *   - contains `settle`             → native ordered-flow, replay as-is;
 *   - neither (dispatch-only)       → strict forced-advance, a conservative
 *                                     ordered-flow trace: replay it with an
 *                                     implied `settle` after any dispatch that
 *                                     produced a survivor.
 */
export type LegacySolverAction = SolverAction | { type: 'advance' };

// ── Canonicalization ─────────────────────────────────────────────────────────

/**
 * Serialize a single attacker into a string key for stable sorting. Two
 * attackers with identical orb contents (ignoring orb order) produce equal
 * keys, so reordering within a stack slot has no hash effect.
 */
function attackerSortKey(a: SolverAttacker): string {
  // Sort orb tuples (color, ammo) so orb order inside the attacker doesn't matter.
  const orbPairs = a.orbs
    .map(o => `${o.color},${o.ammo}`)
    .sort();
  const frozen = a.frozen ?? 0;
  const hammer = a.isHammer ? 1 : 0;
  const mystery = a.mystery ? 1 : 0;
  return `${a.slotCount}|${orbPairs.join(';')}|f=${frozen}|h=${hammer}|m=${mystery}`;
}

/**
 * Returns a new array containing the stack's attackers in canonical order.
 * The stack is semantically a multiset: two states that differ only in
 * stack-slot position should hash identically.
 *
 * Sort keys:
 *   1. Serialized orbs tuple (sorted within the attacker).
 *   2. slotCount.
 *   3. Frozen / hammer / mystery flags.
 *
 * The `id` field is deliberately NOT part of the sort key — for solver
 * equivalence two attackers with identical orbs are interchangeable. We
 * break ties by id only to get a stable, deterministic ordering.
 */
export function canonicalizeStack(stack: SolverAttacker[]): SolverAttacker[] {
  const copy = stack.slice();
  copy.sort((x, y) => {
    const kx = attackerSortKey(x);
    const ky = attackerSortKey(y);
    if (kx < ky) return -1;
    if (kx > ky) return 1;
    // Tie-break on id for deterministic ordering when two interchangeable
    // attackers share the same observable fields.
    return x.id - y.id;
  });
  return copy;
}

// ── Zobrist hashing ──────────────────────────────────────────────────────────

/**
 * Seeded 64-bit PRNG (splitmix64). Used to precompute Zobrist constants.
 * Deterministic: same seed always produces the same sequence.
 */
function splitmix64(seed: bigint): () => bigint {
  let state = seed;
  const MASK = (1n << 64n) - 1n;
  return (): bigint => {
    state = (state + 0x9e3779b97f4a7c15n) & MASK;
    let z = state;
    z = ((z ^ (z >> 30n)) * 0xbf58476d1ce4e5b9n) & MASK;
    z = ((z ^ (z >> 27n)) * 0x94d049bb133111ebn) & MASK;
    z = (z ^ (z >> 31n)) & MASK;
    return z;
  };
}

/**
 * Deterministically derive a 64-bit constant from a string label. Used so
 * that e.g. `hashForCell(7, 2)` is always the same constant across runs,
 * without having to preallocate tables sized for the maximum board.
 *
 * FNV-1a over UTF-16 code units → splitmix64 to improve distribution.
 */
const ZOBRIST_SEED = 0xc0ffee_d00dn;
const zobristRng = splitmix64(ZOBRIST_SEED);

function cryptoHash64(label: string): bigint {
  // FNV-1a 64-bit on a UTF-16 view of the label.
  let h = 0xcbf29ce484222325n;
  const PRIME = 0x100000001b3n;
  const MASK = (1n << 64n) - 1n;
  for (let i = 0; i < label.length; i++) {
    h ^= BigInt(label.charCodeAt(i));
    h = (h * PRIME) & MASK;
  }
  return h;
}

// Memoized Zobrist tables, populated lazily so test-time memory stays bounded.
const cellColorTable = new Map<string, bigint>();
const queueLaneTable = new Map<string, bigint>();
const conveyorTable = new Map<string, bigint>();
const stackTable = new Map<string, bigint>();
const doorSalt = new Map<number, bigint>();
const wallSalt = new Map<number, bigint>();
const barricadeSalt = new Map<number, bigint>();
const megaSalt = new Map<number, bigint>();
let hammerSalt: bigint | null = null;

/**
 * Internal helper: the 64-bit Zobrist constant for (cellIndex, color).
 * Exported so hot-path callers (e.g. AtomicSimulator.simulateAtomicLap) can
 * XOR the delta directly without going through the state-cloning
 * applyCubeCleared helper.
 */
export function getCellColorHash(cellIndex: number, color: number): bigint {
  const key = `c:${cellIndex}:${color}`;
  let v = cellColorTable.get(key);
  if (v === undefined) {
    v = cryptoHash64(key) ^ zobristRng();
    cellColorTable.set(key, v);
  }
  return v;
}

/**
 * Internal helper: the 64-bit mix for a queue-lane slot (laneIndex,
 * queuePositionIndex, attackerHash). Exported for hot-path callers that
 * want to compute the XOR delta for a queue mutation directly.
 */
export function getQueueLaneHash(laneIndex: number, queuePositionIndex: number, attackerHash: bigint): bigint {
  const key = `q:${laneIndex}:${queuePositionIndex}`;
  let base = queueLaneTable.get(key);
  if (base === undefined) {
    base = cryptoHash64(key) ^ zobristRng();
    queueLaneTable.set(key, base);
  }
  // Mix the attacker identity into the slot constant (multiply-shift
  // gives good mixing for bigint XOR).
  return base ^ rotl64(attackerHash, (laneIndex * 7 + queuePositionIndex * 3) % 63);
}

/** Conveyor-slot Zobrist mix. See {@link getQueueLaneHash}. */
export function getConveyorHash(position: number, attackerHash: bigint): bigint {
  const key = `cv:${position}`;
  let base = conveyorTable.get(key);
  if (base === undefined) {
    base = cryptoHash64(key) ^ zobristRng();
    conveyorTable.set(key, base);
  }
  return base ^ rotl64(attackerHash, (position * 5 + 1) % 63);
}

/** Stack-slot Zobrist mix. See {@link getQueueLaneHash}. */
export function getStackHash(position: number, attackerHash: bigint): bigint {
  const key = `s:${position}`;
  let base = stackTable.get(key);
  if (base === undefined) {
    base = cryptoHash64(key) ^ zobristRng();
    stackTable.set(key, base);
  }
  return base ^ rotl64(attackerHash, (position * 11 + 2) % 63);
}

/**
 * Internal helper: the Zobrist salt used for door HP hashing.
 * Exported for the same hot-path reason as {@link getCellColorHash}.
 */
export function getDoorSalt(doorId: number): bigint {
  let v = doorSalt.get(doorId);
  if (v === undefined) {
    v = cryptoHash64(`door:${doorId}`) ^ zobristRng();
    doorSalt.set(doorId, v);
  }
  return v;
}

/**
 * Internal helper: the Zobrist salt used for wall HP hashing.
 * Exported for the same hot-path reason as {@link getCellColorHash}.
 */
export function getWallSalt(wallId: number): bigint {
  let v = wallSalt.get(wallId);
  if (v === undefined) {
    v = cryptoHash64(`wall:${wallId}`) ^ zobristRng();
    wallSalt.set(wallId, v);
  }
  return v;
}

/**
 * Internal helper: the Zobrist salt used for barricade HP hashing.
 * Exported for the same hot-path reason as {@link getCellColorHash}.
 */
export function getBarricadeSalt(cellId: number): bigint {
  let v = barricadeSalt.get(cellId);
  if (v === undefined) {
    v = cryptoHash64(`barr:${cellId}`) ^ zobristRng();
    barricadeSalt.set(cellId, v);
  }
  return v;
}

/**
 * Internal helper: the Zobrist salt used for mega-block HP hashing.
 * Exported for the same hot-path reason as {@link getCellColorHash}.
 */
export function getMegaSalt(megaId: number): bigint {
  let v = megaSalt.get(megaId);
  if (v === undefined) {
    v = cryptoHash64(`mega:${megaId}`) ^ zobristRng();
    megaSalt.set(megaId, v);
  }
  return v;
}

function getHammerSalt(): bigint {
  if (hammerSalt === null) {
    hammerSalt = cryptoHash64('hammers') ^ zobristRng();
  }
  return hammerSalt;
}

/** 64-bit rotate-left for bigints. Used to de-correlate position-specific XORs. */
function rotl64(v: bigint, n: number): bigint {
  const MASK = (1n << 64n) - 1n;
  const shift = BigInt(n & 63);
  return (((v << shift) & MASK) | (v >> (64n - shift))) & MASK;
}

/**
 * Multiply HP by a salt to hash it (different HPs → different mix).
 * Exported for hot-path callers that want to compute the XOR delta for an
 * HP change without cloning the whole state.
 */
export function mixHp(salt: bigint, hp: number): bigint {
  // Rotate-and-multiply mixing: HP=0 → 0, HP=k → distinct for each k.
  if (hp === 0) return 0n;
  const MASK = (1n << 64n) - 1n;
  return (rotl64(salt, (hp * 13) & 63) * (BigInt(hp) | 1n)) & MASK;
}

/**
 * Deterministic 64-bit hash of a single attacker's identity. Used as the
 * "content" mixed in at each slot (queue / conveyor / stack). Two attackers
 * with identical observable fields produce the same attackerHash.
 *
 * Memoized via WeakMap. Attackers are immutable after construction in the
 * solver's transition functions (every mutation produces a fresh object),
 * so caching by object identity is safe. A cache hit returns in O(1)
 * without re-sorting orbs or re-hashing the string. Misses populate the
 * entry. Because WeakMap keys don't retain objects, memory is bounded by
 * the number of live attacker references.
 */
const attackerHashCache: WeakMap<SolverAttacker, bigint> = new WeakMap();
export function attackerHash(a: SolverAttacker): bigint {
  const cached = attackerHashCache.get(a);
  if (cached !== undefined) return cached;
  // Sort orb tuples so order within the attacker doesn't affect the hash.
  const pairs = a.orbs.map(o => `${o.color},${o.ammo}`).sort();
  const frozen = a.frozen ?? 0;
  // `age` is ring-only deferral state (absent = 0 everywhere else). It MUST be
  // hashed: two states differing only in how long a rider has been riding have
  // different futures (the age bound forces the front's arrival), so they must
  // not transposition-collide. Every age mutation builds a fresh attacker
  // object, which is what keeps the WeakMap memoisation sound.
  const key = `ah|sc=${a.slotCount}|orbs=${pairs.join(';')}|f=${frozen}|h=${a.isHammer ? 1 : 0}|m=${a.mystery ? 1 : 0}|a=${a.age ?? 0}`;
  const v = cryptoHash64(key);
  attackerHashCache.set(a, v);
  return v;
}

/**
 * Compute the full Zobrist hash of a {@link SolverState}. The stack is
 * canonicalized before hashing so permutations within the stack collide.
 *
 * This is an O(N) pass over every observable feature; subsequent state
 * changes should use `applyCubeCleared` (or similar incremental helpers)
 * to update the hash in O(1) per change.
 */
export function hashState(state: SolverState): bigint {
  let h = 0n;

  // Board: XOR in (cellIndex, color) constants for every non-empty cell.
  for (let i = 0; i < state.board.length; i++) {
    const v = state.board[i];
    if (v !== 0) h ^= getCellColorHash(i, v);
  }

  // Queue lanes: XOR in (laneIndex, queuePositionIndex, attackerHash).
  for (let lane = 0; lane < state.queueLanes.length; lane++) {
    const entries = state.queueLanes[lane];
    for (let pos = 0; pos < entries.length; pos++) {
      h ^= getQueueLaneHash(lane, pos, attackerHash(entries[pos]));
    }
  }

  // In-flight ring: XOR in (position, attackerHash), POSITIONALLY — unlike the
  // stack, the ring is not a multiset. Its order is FIFO arrival order and
  // decides who settles (or is force-arrived) next, so two states differing
  // only in ring order are genuinely different states and must hash apart.
  // Each rider's age is inside its attackerHash, so deferral is hashed too.
  for (let i = 0; i < state.conveyor.length; i++) {
    h ^= getConveyorHash(i, attackerHash(state.conveyor[i]));
  }

  // Stack: canonicalize before hashing so permutations collide.
  const canonStack = canonicalizeStack(state.stack);
  for (let i = 0; i < canonStack.length; i++) {
    h ^= getStackHash(i, attackerHash(canonStack[i]));
  }

  // Doors / walls / barricades: XOR in HP-mixed salts.
  for (const idStr of Object.keys(state.doorHp)) {
    const id = Number(idStr);
    h ^= mixHp(getDoorSalt(id), state.doorHp[id]);
  }
  for (const idStr of Object.keys(state.wallHp)) {
    const id = Number(idStr);
    h ^= mixHp(getWallSalt(id), state.wallHp[id]);
  }
  for (const cellStr of Object.keys(state.barricadeHp)) {
    const cell = Number(cellStr);
    h ^= mixHp(getBarricadeSalt(cell), state.barricadeHp[cell]);
  }
  for (const idStr of Object.keys(state.megaHp)) {
    const id = Number(idStr);
    h ^= mixHp(getMegaSalt(id), state.megaHp[id]);
  }

  // Hammers: mix count into a single salt.
  if (state.remainingHammers !== 0) {
    h ^= mixHp(getHammerSalt(), state.remainingHammers);
  }

  return h;
}

// ── Incremental state updates ────────────────────────────────────────────────

/**
 * Return a new state with the given cell set to empty (color=0). Also
 * returns the updated hash, computed incrementally from `oldHash` by
 * XOR-ing out the old cell contribution.
 *
 * Does NOT mutate the input state.
 */
export function applyCubeCleared(
  state: SolverState,
  cellIndex: number,
  oldHash: bigint,
): { state: SolverState; hash: bigint } {
  const oldColor = state.board[cellIndex];
  if (oldColor === 0) {
    // No-op: cell already empty. Return caller's state unchanged.
    return { state, hash: oldHash };
  }
  const newBoard = new Uint16Array(state.board);
  newBoard[cellIndex] = 0;
  const delta = getCellColorHash(cellIndex, oldColor);
  const newHash = oldHash ^ delta;
  return {
    state: { ...state, board: newBoard, boardHash: state.boardHash ^ delta },
    hash: newHash,
  };
}

/**
 * Return a new state with the given door's HP decremented (or removed if HP=0).
 * Hash is updated incrementally.
 */
export function applyDoorHpChange(
  state: SolverState,
  doorId: number,
  newHp: number,
  oldHash: bigint,
): { state: SolverState; hash: bigint } {
  const oldHp = state.doorHp[doorId] ?? 0;
  if (oldHp === newHp) return { state, hash: oldHash };
  const salt = getDoorSalt(doorId);
  let h = oldHash ^ mixHp(salt, oldHp);
  h ^= mixHp(salt, newHp);
  const nextDoors = { ...state.doorHp };
  if (newHp <= 0) delete nextDoors[doorId];
  else nextDoors[doorId] = newHp;
  return { state: { ...state, doorHp: nextDoors }, hash: h };
}

/** Counterpart to {@link applyDoorHpChange} for walls. */
export function applyWallHpChange(
  state: SolverState,
  wallId: number,
  newHp: number,
  oldHash: bigint,
): { state: SolverState; hash: bigint } {
  const oldHp = state.wallHp[wallId] ?? 0;
  if (oldHp === newHp) return { state, hash: oldHash };
  const salt = getWallSalt(wallId);
  let h = oldHash ^ mixHp(salt, oldHp);
  h ^= mixHp(salt, newHp);
  const next = { ...state.wallHp };
  if (newHp <= 0) delete next[wallId];
  else next[wallId] = newHp;
  return { state: { ...state, wallHp: next }, hash: h };
}

/** Counterpart to {@link applyDoorHpChange} for barricades. */
export function applyBarricadeHpChange(
  state: SolverState,
  cellId: number,
  newHp: number,
  oldHash: bigint,
): { state: SolverState; hash: bigint } {
  const oldHp = state.barricadeHp[cellId] ?? 0;
  if (oldHp === newHp) return { state, hash: oldHash };
  const salt = getBarricadeSalt(cellId);
  // Barricade HP factors into the board-only hash too (barricades with HP>0
  // are invisible to `firstVisible`, so changing their HP can change reach).
  const delta = mixHp(salt, oldHp) ^ mixHp(salt, newHp);
  const h = oldHash ^ delta;
  const next = { ...state.barricadeHp };
  if (newHp <= 0) delete next[cellId];
  else next[cellId] = newHp;
  return { state: { ...state, barricadeHp: next, boardHash: state.boardHash ^ delta }, hash: h };
}

/** Counterpart to {@link applyDoorHpChange} for mega blocks. */
export function applyMegaHpChange(
  state: SolverState,
  megaId: number,
  newHp: number,
  oldHash: bigint,
): { state: SolverState; hash: bigint } {
  const oldHp = state.megaHp[megaId] ?? 0;
  if (oldHp === newHp) return { state, hash: oldHash };
  const salt = getMegaSalt(megaId);
  let h = oldHash ^ mixHp(salt, oldHp);
  h ^= mixHp(salt, newHp);
  const next = { ...state.megaHp };
  if (newHp <= 0) delete next[megaId];
  else next[megaId] = newHp;
  return { state: { ...state, megaHp: next }, hash: h };
}

/** Decrement the hammer count by one; updates hash incrementally. */
export function applyHammerConsumed(
  state: SolverState,
  oldHash: bigint,
): { state: SolverState; hash: bigint } {
  if (state.remainingHammers <= 0) return { state, hash: oldHash };
  const salt = getHammerSalt();
  let h = oldHash ^ mixHp(salt, state.remainingHammers);
  const next = state.remainingHammers - 1;
  h ^= mixHp(salt, next);
  return { state: { ...state, remainingHammers: next }, hash: h };
}

// ── Initial state construction ───────────────────────────────────────────────

/**
 * Per-level parameters for building an initial state.
 */
export interface InitialStateOptions {
  level: ImageLevel;
  /**
   * Queue-generation seed. Omit to use the level's own seed
   * ({@link resolveLevelSeed} — `gen.seed`, else `level.seed`, else 42), which
   * is exactly what the runtime generates from; pass one explicitly only to
   * search *other* seeds (audit sweeps, calibration probes).
   */
  seed?: number;
  numQueues: number;
  stackSlots: number;
  conveyorCap: number;
}

/**
 * Build the starting state for a level. Reuses {@link generateRawQueue} so
 * the solver sees the exact same attackers the runtime / audit would see.
 *
 * The ImageLevel grid is -1=empty, 0..N=color. We encode as Uint16Array
 * with 0=empty, 1+=color+1 for the board array.
 */
/**
 * Everything {@link buildSolverState} needs, with the queue already generated.
 *
 * `initialState` fills this in from an {@link ImageLevel}; `GameScene` fills it
 * in from the systems it has ALREADY built (padded grid, spliced attacker list,
 * auto-picked door/wall colours), so the live game's logical model is built
 * from the very objects it renders rather than from a second reading of the
 * level file. Anything that differs between those two readings — grid padding,
 * `LevelGenerator.fromGrid`'s single-colour safeguard, `autoPickColors` — would
 * otherwise be a silent divergence between the model and the game.
 */
export interface BuildSolverStateOptions {
  /** Board as the runtime holds it: one stack per cell, empty = `[]`. */
  cellGrid: CellStack[][];
  /** Attackers in queue order, ids already assigned (hammers included). */
  attackers: SolverAttacker[];
  /** Snake cells as `row * boardWidth + col`. */
  snakeCells?: Iterable<number>;
  megaBlocks?: MegaBlockDef[];
  lockedDoors?: LockedDoorDef[];
  blockadeWalls?: BlockadeWallDef[];
  barricades?: BarricadeDef[];
  numQueues: number;
  stackSlots: number;
  conveyorCap: number;
}

/**
 * Assemble a {@link SolverState} from an already-resolved board + attacker list.
 *
 * Split out of {@link initialState} (which now delegates to it) so the live game
 * can build the identical structure from its own runtime data. Order matters and
 * is preserved from the original: base cells first, then mega colours painted
 * over their footprints, then snake cells derived from the resulting board.
 */
export function buildSolverState(opts: BuildSolverStateOptions): SolverState {
  const { cellGrid, attackers, numQueues, stackSlots, conveyorCap } = opts;

  const boardHeight = cellGrid.length;
  const boardWidth = boardHeight > 0 ? cellGrid[0].length : 0;

  // Pack into Uint16Array: 0=empty, 1+=color index + 1.
  const board = new Uint16Array(boardWidth * boardHeight);
  for (let r = 0; r < boardHeight; r++) {
    for (let c = 0; c < boardWidth; c++) {
      const stack = cellGrid[r][c];
      if (stack.length > 0) {
        // For the solver we care about the top cube only — snake/stacks of
        // depth > 1 aren't present in ImageLevel data (always depth 1 here).
        board[r * boardWidth + c] = (stack[stack.length - 1] + 1) & 0xffff;
      }
    }
  }

  // Distribute attackers into lanes via the shared packer that mirrors
  // QueueSystem's padding-and-collapse logic (see QueueSystem.ts:14-47).
  const queueLanes = packQueueLanes(attackers, numQueues);

  // Door / wall / barricade HP maps. IDs are 1-based and assigned in
  // source order (matching the order BoardSystem uses internally).
  const doorHp: Record<number, number> = {};
  const doorCells = new Set<number>();
  const doorIdByCell: Record<number, number> = {};
  const doorColorById: Record<number, number> = {};
  if (opts.lockedDoors) {
    for (let i = 0; i < opts.lockedDoors.length; i++) {
      const d: LockedDoorDef = opts.lockedDoors[i];
      const id = i + 1;
      doorHp[id] = d.hp;
      doorColorById[id] = d.colorIndex ?? 0;
      for (let r = d.row; r < d.row + d.height; r++) {
        for (let c = d.col; c < d.col + d.width; c++) {
          if (r >= boardHeight || c >= boardWidth) continue;
          const idx = r * boardWidth + c;
          doorCells.add(idx);
          doorIdByCell[idx] = id;
        }
      }
    }
  }

  const wallHp: Record<number, number> = {};
  const wallInfoById: Record<number, { side: 'top' | 'bottom' | 'left' | 'right'; startCell: number; length: number; colorIndex: number }> = {};
  if (opts.blockadeWalls) {
    for (let i = 0; i < opts.blockadeWalls.length; i++) {
      const w: BlockadeWallDef = opts.blockadeWalls[i];
      const id = i + 1;
      wallHp[id] = w.hp;
      wallInfoById[id] = {
        side: w.side,
        startCell: w.startCell,
        length: w.length,
        colorIndex: w.colorIndex ?? 0,
      };
    }
  }

  const barricadeHp: Record<number, number> = {};
  const barricadeCellsById: Record<number, number[]> = {};
  const barricadeIdByCell: Record<number, number> = {};
  const barricadeIds: number[] = [];
  if (opts.barricades) {
    for (let i = 0; i < opts.barricades.length; i++) {
      const b: BarricadeDef = opts.barricades[i];
      const id = i + 1;
      barricadeIds.push(id);
      const cells: number[] = [];
      for (let r = b.row; r < b.row + b.height; r++) {
        for (let c = b.col; c < b.col + b.width; c++) {
          if (r >= boardHeight || c >= boardWidth) continue;
          const idx = r * boardWidth + c;
          barricadeHp[idx] = b.hp;
          cells.push(idx);
          barricadeIdByCell[idx] = id;
        }
      }
      barricadeCellsById[id] = cells;
    }
  }

  // Mega block HP + cell map. Populate board array at the mega cells with
  // the mega's color (BoardSystem does the same: `this.grid[r][c] = [def.colorIndex]`).
  const megaHp: Record<number, number> = {};
  const megaCellsById: Record<number, number[]> = {};
  const megaIdByCell: Record<number, number> = {};
  if (opts.megaBlocks) {
    for (let i = 0; i < opts.megaBlocks.length; i++) {
      const mb: MegaBlockDef = opts.megaBlocks[i];
      const id = i + 1; // 1-based, matches BoardOverlay allocation order
      megaHp[id] = mb.hp;
      const cells: number[] = [];
      for (let r = mb.row; r < mb.row + mb.height; r++) {
        for (let c = mb.col; c < mb.col + mb.width; c++) {
          if (r >= boardHeight || c >= boardWidth) continue;
          const idx = r * boardWidth + c;
          cells.push(idx);
          megaIdByCell[idx] = id;
          // Ensure the board carries the mega's color at every footprint cell.
          board[idx] = (mb.colorIndex + 1) & 0xffff;
        }
      }
      megaCellsById[id] = cells;
    }
  }

  // Snake cells: only cells that actually hold a cube count.
  const snakeCells = new Set<number>();
  for (const idx of opts.snakeCells ?? []) {
    if (idx >= 0 && idx < board.length && board[idx] !== 0) snakeCells.add(idx);
  }

  // Count hammers across queue + (always empty at init) conveyor + stack.
  const remainingHammers = attackers.filter(a => a.isHammer).length;

  const meta: SolverLevelMeta = {
    snakeCells,
    doorCells,
    doorIdByCell,
    doorColorById,
    megaCellsById,
    megaIdByCell,
    barricadeCellsById,
    barricadeIdByCell,
    barricadeIds,
    wallInfoById,
  };

  // Precompute the board-only Zobrist hash for the reach-colors cache. The
  // hash is maintained incrementally by every board/barricade mutation in
  // the simulator so the search hot path never pays for this full scan
  // again.
  let boardHash = 0n;
  for (let i = 0; i < board.length; i++) {
    const v = board[i];
    if (v !== 0) boardHash ^= getCellColorHash(i, v);
  }
  for (const cellStr of Object.keys(barricadeHp)) {
    const cell = Number(cellStr);
    const hp = barricadeHp[cell];
    if (hp > 0) boardHash ^= mixHp(getBarricadeSalt(cell), hp);
  }

  return {
    board,
    boardWidth,
    boardHeight,
    queueLanes,
    conveyor: [],
    stack: [],
    doorHp,
    wallHp,
    barricadeHp,
    megaHp,
    meta,
    remainingHammers,
    conveyorCap,
    stackCap: stackSlots,
    boardHash,
  };
}

export function initialState(opts: InitialStateOptions): SolverState {
  const { level, numQueues, stackSlots, conveyorCap } = opts;
  // Resolve the seed the same way the runtime does, so an unqualified proof is
  // a proof about the queue the player actually gets. An explicit `opts.seed`
  // still wins — that is how seed sweeps ask about a different queue.
  const seed = opts.seed ?? resolveLevelSeed(level);

  // Convert the level grid (-1=empty, 0..N=color) into CellStack[][].
  // Each non-empty cell is a single-color stack [color]. This matches what
  // LevelGenerator.fromGrid does internally for image levels.
  const cellGrid: CellStack[][] = level.grid.map(row =>
    row.map(val => (val < 0 ? [] : [val])),
  );

  const boardHeight = cellGrid.length;
  const boardWidth = boardHeight > 0 ? cellGrid[0].length : 0;

  // The level's proven `gen.config` overlaid on its embedded difficulty, else
  // the embedded difficulty alone, else the mid-game tier table (what the audit
  // / ArtPipeline do for standalone boards). Shared with the runtime and BOTH
  // audit paths so a proof is a proof about the queue the player gets.
  const difficulty: DifficultyConfig = resolveLevelDifficulty(
    level,
    NEW_LEVEL_DIFFICULTY_LEVEL,
  );

  const rawQueue: AttackerQueueEntry[] = generateRawQueue(
    cellGrid,
    seed,
    difficulty,
    level.megaBlocks,
    level.blockadeWalls,
  );

  // Apply frozenIndices from the level file (if any).
  if (level.frozenIndices) {
    for (const fi of level.frozenIndices) {
      if (fi.index >= 0 && fi.index < rawQueue.length) {
        rawQueue[fi.index].frozen = fi.counter;
      }
    }
  }

  // Insert hammers at the specified positions. Hammers break barricades
  // and are modeled as special SolverAttackers with isHammer=true.
  const hammerIndices = (level.hammerIndices ?? []).slice().sort((a, b) => b - a);
  const attackers: SolverAttacker[] = rawQueue.map((entry, idx) => {
    const isMulti = !!entry.orbs && entry.orbs.length > 1;
    const orbs = isMulti
      ? entry.orbs!.map(o => ({ color: o.colorIndex, ammo: o.ammo }))
      : splitSingleColorAmmo(entry.colorIndex, entry.ammo, entry.size);
    const attacker: SolverAttacker = {
      id: idx + 1, // stable 1-based id
      orbs,
      slotCount: orbs.length,
    };
    if (entry.frozen && entry.frozen > 0) attacker.frozen = entry.frozen;
    if (entry.mystery) attacker.mystery = true;
    return attacker;
  });

  // Insert hammer items (descending order so indices don't shift).
  let nextId = attackers.length + 1;
  for (const idx of hammerIndices) {
    const clamped = Math.min(idx, attackers.length);
    attackers.splice(clamped, 0, {
      id: nextId++,
      orbs: [{ color: -1, ammo: 0 }],
      slotCount: 1,
      isHammer: true,
    });
  }

  // Snake cells: column-based. Every cell in a snake column is a candidate;
  // `buildSolverState` keeps the ones that actually hold a cube (mega colours
  // are painted before that filter runs, exactly as before).
  const snakeCells: number[] = [];
  if (level.snakeCols) {
    for (const c of level.snakeCols) {
      if (c < 0 || c >= boardWidth) continue;
      for (let r = 0; r < boardHeight; r++) snakeCells.push(r * boardWidth + c);
    }
  }

  return buildSolverState({
    cellGrid,
    attackers,
    snakeCells,
    megaBlocks: level.megaBlocks,
    lockedDoors: level.lockedDoors,
    blockadeWalls: level.blockadeWalls,
    barricades: level.barricades,
    numQueues,
    stackSlots,
    conveyorCap,
  });
}

/** Split a single-color attacker's total ammo evenly across its orbs. */
function splitSingleColorAmmo(color: number, totalAmmo: number, size: number): { color: number; ammo: number }[] {
  const safeSize = Math.min(size, Math.max(totalAmmo, 1));
  const base = Math.floor(totalAmmo / safeSize);
  let remainder = totalAmmo % safeSize;
  const out: { color: number; ammo: number }[] = [];
  for (let i = 0; i < safeSize; i++) {
    const ammo = base + (remainder > 0 ? 1 : 0);
    if (remainder > 0) remainder--;
    out.push({ color, ammo });
  }
  return out;
}

/**
 * Mirror of `QueueSystem.findBestLanes` — pack a multi-orb attacker into
 * adjacent lanes that minimise the max depth, so orbs start at the same
 * depth in all their lanes.
 */
function findBestLaneStart(laneDepths: number[], size: number): number {
  let bestStart = 0;
  let bestMaxDepth = Infinity;
  const numLanes = laneDepths.length;
  for (let start = 0; start <= numLanes - size; start++) {
    let maxD = 0;
    for (let i = 0; i < size; i++) {
      maxD = Math.max(maxD, laneDepths[start + i]);
    }
    if (maxD < bestMaxDepth) {
      bestMaxDepth = maxD;
      bestStart = start;
    }
  }
  return bestStart;
}

/**
 * Pack a list of attackers into `numQueues` lanes using the exact same
 * policy as `QueueSystem`'s constructor (see QueueSystem.ts:14-47).
 *
 * Runtime logic: for each attacker, pick the adjacent lane range that
 * minimises max depth, pad shorter target lanes with null placeholders
 * so the attacker starts at the same depth in every target lane, then
 * push the attacker. After all attackers are placed, nulls are collapsed
 * away so each lane is a dense sequence of attackers.
 *
 * For the solver we don't need the null placeholders — the final per-lane
 * content (after collapse) is just the attackers in push order. But we
 * MUST track `laneDepths` as if padding happened, because the depth
 * record is what drives `findBestLaneStart` for subsequent attackers.
 * Concretely: after placing a multi-slot attacker whose target lanes have
 * mixed prior depths [a, b, ...], every target lane's recorded depth
 * becomes `max(a, b, ...) + 1`, which is what the runtime records after
 * padding each shorter lane up to `maxDepth` and pushing.
 *
 * An earlier version of this helper incorrectly set each target lane's
 * depth to its own post-push length (i.e. `prior_depth + 1`), which
 * diverged from the runtime for any attacker sequence where a multi-slot
 * attacker's target lanes had different prior depths. The solver then
 * explored a different queue topology than the one the player actually
 * sees, silently producing wrong verdicts.
 */
export function packQueueLanes<T extends { slotCount: number }>(
  attackers: T[],
  numQueues: number,
): T[][] {
  const queueLanes: T[][] = Array.from({ length: numQueues }, () => []);
  const laneDepths = Array(numQueues).fill(0);
  for (const a of attackers) {
    const size = a.slotCount;
    const start = findBestLaneStart(laneDepths, size);
    // maxDepth = the row at which this attacker lands in every target
    // lane (after the runtime would have padded shorter lanes up to it).
    let maxDepth = 0;
    for (let i = 0; i < size; i++) {
      maxDepth = Math.max(maxDepth, laneDepths[start + i]);
    }
    for (let i = 0; i < size; i++) {
      const lane = start + i;
      queueLanes[lane].push(a);
      laneDepths[lane] = maxDepth + 1;
    }
  }
  return queueLanes;
}

// ── Test-only utilities ──────────────────────────────────────────────────────

/**
 * Clone a state so callers can mutate without affecting the original.
 * Mainly useful for tests that want to compare "hash after a change" vs
 * "hash of a freshly-built changed state".
 */
export function cloneState(state: SolverState): SolverState {
  return {
    board: new Uint16Array(state.board),
    boardWidth: state.boardWidth,
    boardHeight: state.boardHeight,
    // Deep-copy queue lanes so tests mutating one don't clobber the other.
    queueLanes: state.queueLanes.map(lane => lane.slice()),
    conveyor: state.conveyor.slice(),
    stack: state.stack.slice(),
    doorHp: { ...state.doorHp },
    wallHp: { ...state.wallHp },
    barricadeHp: { ...state.barricadeHp },
    megaHp: { ...state.megaHp },
    meta: state.meta, // shared ref — meta is immutable across play
    remainingHammers: state.remainingHammers,
    conveyorCap: state.conveyorCap,
    stackCap: state.stackCap,
    boardHash: state.boardHash,
  };
}
